import { InfoCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popover,
  Row,
  Select,
  Spin,
  TreeSelect,
} from "antd";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GetApproversApi } from "../../../api/approvals/GetApproversApi";
import CreateTopPlanApi from "../../../api/CreateTopPlanApi";
import { GetActiveScalesApi } from "../../../api/GetActiveScalesApi";
import { GetAllPlanGroupsApi } from "../../../api/GetAllPlanGroups";
import GetScaleUnitsByScaleApi from "../../../api/GetScaleUnitsByScaleApi";
import { GetTreeSelectUnitsApi } from "../../../api/GetTreeSelectUnitsApi";
import { GetPerfCycleTopLevelApi } from "../../../api/perf-cycles/GetPerfCycleTopPlanApi";
import { GetUnitPositionsApi2 } from "../../../api/units/GetUnitPositionsApi2";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../../helpers/NotificationHelpers";
import PageLoader from "../../sub/PageLoader";
import { GetEvaluationsApi } from "../../../api/ehrm/GetEvaluationsApi";
import { GetEmployeeEvaluationsApi } from "../../../api/ehrm/GetEmployeeEvaluationsApi";
import { GetGoalGroupsApi } from "../../../api/ehrm/GetGoalGroupsApi";
import PhrmService from "../../../services/PhrmService";

const { Option } = Select;

const { TextArea } = Input;

function AddTopPlanModal(props) {
  const [submit, setSubmit] = useState(false);
  const [unitsTree, setUnitsTree] = useState([]);
  const [topPlan, setTopPlan] = useState();
  const [planGroups, setPlanGroups] = useState([]);
  const [scales, setScales] = useState([]);
  const [scaleUnits, setScaleUnits] = useState([]);
  const [unitPositions, setUnitPositions] = useState([]);
  const [percentage, setPercentage] = useState(false);
  const [addScaleUnit, setAddScaleUnit] = useState(false);
  const [canExceed, setCanExceed] = useState(false);
  const [isPercentageUpdate, setIsPercentageUpdate] = useState(false);
  const [isNegativeScale, setIsNegativeScale] = useState(false);
  const [supportsNegativeScale, setSupportsNegativeScale] = useState(true);
  const perfCycleState = useSelector((state) => state.perfCycleState.value);
  const topPlanState = useSelector((state) => state.topPlanState.value);
  const [form] = Form.useForm();
  const positionState = useSelector((state) => state.positionState.value);
  const [approvers, setApprovers] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [goalGroups, setGoalGroups] = useState([]);
  const [selectedEvalId, setSelectedEvalId] = useState(null);
  const [showEhrmFields, setShowEhrmFields] = useState(false);
  const [assignedUser, setAssignedUser] = useState(null);
  const [isPhrmEnabled, setIsPhrmEnabled] = useState(false);
  const [fieldLabels, setFieldLabels] = useState({});
  const [cascadeFromSplitPlans, setCascadeFromSplitPlans] = useState(false);

  function handleCancel() {
    setSubmit(false);
    setUnitsTree([]);
    setPlanGroups([]);
    setScales([]);
    setScaleUnits([]);
    setUnitPositions([]);
    setPercentage(false);
    setAddScaleUnit(false);
    setTopPlan();
    setCanExceed(false);
    setIsPercentageUpdate(false);
    setIsNegativeScale(false);
    setSupportsNegativeScale(true);
    form.resetFields();
    props.handleCancel();
  }

  useEffect(() => {
    if (props.visible) {
      getUnitsTree();
      getPlanGroups();
      getActiveScales();
      getApprovers();
      checkPhrmPermissions();
      getTopPlanDetails();
    }
  }, [props.visible]);

  async function checkPhrmPermissions() {
    const phrmEnabled = await PhrmService.isPhrmEnabled();
    setIsPhrmEnabled(phrmEnabled);
    setFieldLabels(PhrmService.getFieldLabels(phrmEnabled));
  }

  async function getTopPlanDetails() {
    const apiResponse = await GetPerfCycleTopLevelApi(perfCycleState.id);
    if (apiResponse.success) {
      setTopPlan(apiResponse.data);
    }
  }

  async function getUnitsTree() {
    const apiResponse = await GetTreeSelectUnitsApi();
    if (apiResponse.message === "success") {
      setUnitsTree(apiResponse.units);
    }
  }

  async function getPlanGroups() {
    const apiResponse = await GetAllPlanGroupsApi();
    if (apiResponse.message === "success") {
      setPlanGroups(apiResponse.planGroups);
    }
  }

  async function getActiveScales() {
    const apiResponse = await GetActiveScalesApi();
    if (apiResponse.success) {
      setScales(apiResponse.data);
    }
  }

  function selectPlanGroup(value) {
    planGroups.forEach((group) => {
      if (value === group.id) {
        form.setFields([
          {
            name: "targetScale",
            value: group.scale?.id,
          },
        ]);
        selectScale(group.scale?.id);
      }
    });
  }

  async function selectUnit(value) {
    const apiResponse = await GetUnitPositionsApi2(value);
    if (apiResponse.success) {
      setUnitPositions(apiResponse.data);
      form.setFields([
        {
          name: "assignTo",
          value: "",
        },
      ]);
    }
  }

  async function selectAssignTo(positionId) {
    // Find the position and its user
    const position = unitPositions.find(p => p.id === positionId);
    
    console.log('Selected position:', position);
    console.log('Position user:', position?.user);
    console.log('Employee Reference ID:', position?.user?.empReferrenceId);
    
    if (position && position.user) {
      setAssignedUser(position.user);
      
      // Set the employee reference field immediately
      form.setFieldsValue({
        empReference: position.user.empReferrenceId || '',
      });
      
      // If user has empReferrenceId, fetch evaluations
      if (position.user.empReferrenceId) {
        console.log('Fetching evaluations for employee:', position.user.empReferrenceId);
        const evalResponse = await GetEmployeeEvaluationsApi(position.user.empReferrenceId);
        console.log('Evaluation response:', evalResponse);
        
        if (evalResponse.ok && evalResponse.data) {
          setEvaluations(evalResponse.data);
          console.log('Evaluations set:', evalResponse.data);
        } else {
          setEvaluations([]);
          console.log('No evaluations found or error');
        }
      } else {
        setEvaluations([]);
        console.log('No empReferrenceId found for user');
      }
    } else {
      setAssignedUser(null);
      setEvaluations([]);
      form.setFieldsValue({
        empReference: '',
      });
    }
    
    // Clear EHRM dropdown fields when assignTo changes
    form.setFieldsValue({
      ehrmEvalId: undefined,
      ehrmGoalTypeId: undefined,
      ehrmGoalWeightage: undefined,
    });
    setSelectedEvalId(null);
    setGoalGroups([]);
  }

  async function selectEvaluation(evalId) {
    console.log('Evaluation selected:', evalId);
    setSelectedEvalId(evalId);
    
    // Fetch goal groups for this evaluation
    console.log('Fetching goal groups for evaluation:', evalId);
    const goalGroupResponse = await GetGoalGroupsApi(evalId);
    console.log('Goal group response:', goalGroupResponse);
    
    if (goalGroupResponse.ok && goalGroupResponse.data) {
      setGoalGroups(goalGroupResponse.data);
      console.log('Goal groups set:', goalGroupResponse.data);
    } else {
      setGoalGroups([]);
      console.log('No goal groups found or error');
    }
    
    // Clear goal type when evaluation changes
    form.setFieldsValue({
      ehrmGoalTypeId: undefined,
    });
  }

  async function selectScale(value) {
    setScaleUnits([]);
    let addUnit = "";
    scales.forEach((scale) => {
      if (scale.id === value) {
        if (scale.name === "Number") {
          setAddScaleUnit(false);
          setPercentage(false);
          setSupportsNegativeScale(true); // Number supports negative scale
        } else if (scale.name === "Percentage") {
          setAddScaleUnit(false);
          setPercentage(true);
          setSupportsNegativeScale(true); // Percentage supports negative scale
        } else if (scale.name === "Currency") {
          setAddScaleUnit(true);
          setPercentage(false);
          setSupportsNegativeScale(true); // Currency supports negative scale
          addUnit = "add";
        } else {
          setAddScaleUnit(true);
          setPercentage(false);
          setSupportsNegativeScale(false); // Other scales don't support negative scale
          // If current scale doesn't support negative scale, disable it
          if (isNegativeScale) {
            setIsNegativeScale(false);
          }
          addUnit = "add";
        }
      }
    });
    if (addUnit === "add") {
      const apiResponse = await GetScaleUnitsByScaleApi(value);
      if (apiResponse.message === "success") {
        setScaleUnits(apiResponse.scaleUnits);
      }
      apiResponse.scaleUnits?.forEach((unit) => {
        if (unit.default) {
          form.setFields([
            {
              name: "unitScale",
              value: unit.id,
            },
          ]);
        }
      });
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    let attributesValues = [];
    topPlan?.planLevelAttributes?.forEach((attribute) => {
      let attributeValue = values[attribute.id];
      
      // Handle dropdown values - skip empty arrays
      if (attribute.valueType === "dropdown") {
        if (Array.isArray(attributeValue) && attributeValue.length === 0) {
          // Skip empty dropdown selections
          return;
        }
      }
      
      // Skip undefined or null values
      if (attributeValue === undefined || attributeValue === null || attributeValue === "") {
        return;
      }
      
      attributesValues.push({
        attributeId: attribute.id,
        value: attributeValue,
      });
    });
    // Only send EHRM fields if PHRM is enabled and fields are filled
    const shouldSendToEhrm = isPhrmEnabled && 
      values.ehrmEvalId && 
      values.ehrmGoalTypeId && 
      values.ehrmGoalWeightage;

    console.log('EHRM Debug - Plan Creation:', {
      isPhrmEnabled,
      ehrmEvalId: values.ehrmEvalId,
      ehrmGoalTypeId: values.ehrmGoalTypeId,
      ehrmGoalWeightage: values.ehrmGoalWeightage,
      shouldSendToEhrm,
      allValues: values
    });

    const apiResponse = await CreateTopPlanApi(
      values.title,
      values.description || "",
      values.unit,
      values.target,
      values.targetDate || "",
      values.targetScale,
      addScaleUnit ? values.unitScale || null : null,
      values.assignTo,
      perfCycleState.id,
      values.planGroup,
      canExceed,
      attributesValues,
      positionState.id,

      positionState?.userRole?.name === "Manager"
        ? values.approver
        : positionState.id,
      
      shouldSendToEhrm ? values.ehrmEvalId : undefined,
      shouldSendToEhrm ? values.ehrmGoalTypeId : undefined,
      shouldSendToEhrm ? values.ehrmGoalWeightage : undefined,
      values.cascadeFromSplitPlans || false,
      isPercentageUpdate,
      isNegativeScale
    );

    if (apiResponse.statusCode) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", `${topPlanState} added`);

      props.handleOk(apiResponse.data);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't add plan. Try again later."
      );
    }
    handleCancel();
  }

  async function getApprovers() {
    const apiResponse = await GetApproversApi(positionState?.id);

    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      setApprovers(apiResponse?.data);
      form.setFields([
        {
          name: "approver",
          value: apiResponse?.data[0]?.id || "",
        },
      ]);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't edit approver. Try again later."
      );
    }
  }

  const unitSelector = (
    <Form.Item name="unitScale" noStyle>
      <Select style={{ width: 100 }}>
        {scaleUnits.map((unit) => {
          return (
            <Option key={unit.id} value={unit.id}>
              {unit.abbreviation}
            </Option>
          );
        })}
      </Select>
    </Form.Item>
  );

  function renderAttributes() {
    let output = [];

    topPlan?.planLevelAttributes?.forEach((attribute) => {
      output.push(
        <Col span={12} key={attribute.id}>
          <Form.Item label={attribute.name} name={attribute.id}>
            {attribute.valueType === "text" ? (
              <Input style={{ width: "100%" }} placeholder={attribute.name} />
            ) : attribute.valueType === "number" ? (
              <InputNumber
                min={0}
                style={{ width: "100%" }}
                placeholder={attribute.name}
              />
            ) : attribute.valueType === "double" ? (
              <InputNumber
                min={attribute.minValue || 0}
                max={attribute.maxValue || undefined}
                step={attribute.decimalPrecision ? 1 / Math.pow(10, attribute.decimalPrecision) : 0.01}
                precision={attribute.decimalPrecision || 2}
                style={{ width: "100%" }}
                placeholder={attribute.name}
              />
            ) : attribute.valueType === "dropdown" ? (
              <Select
                mode="multiple"
                showSearch
                placeholder={`Select ${attribute.name}`}
                style={{ width: "100%", maxWidth: "300px" }}
                getPopupContainer={(trigger) => trigger.parentNode}
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                }
              >
                {attribute.dropdownOptions?.map((option) => (
                  <Option key={option.id} value={option.id}>
                    {option.value}
                  </Option>
                ))}
              </Select>
            ) : attribute.valueType === "date" ? (
              <DatePicker style={{ width: "100%" }} placeholder={attribute.name} />
            ) : (
              <Input style={{ width: "100%" }} placeholder={attribute.name} />
            )}
          </Form.Item>
        </Col>
      );
    });

    return output;
  }

  return (
    <>
      <Modal
        title={`Add ${topPlanState}`}
        visible={props.visible}
        onCancel={() => handleCancel()}
        footer={null}
        destroyOnClose={true}
        width={900}
      >
        <Form
          name="addPlan"
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          requiredMark={false}
          form={form}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Plan group*"
                name="planGroup"
                rules={[
                  {
                    required: true,
                    message: "Please select a plan group!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select plan group"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                  onChange={(value) => selectPlanGroup(value)}
                >
                  {planGroups.map((group) => {
                    return (
                      <Option key={group.id} value={group.id}>
                        {group.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label={fieldLabels.title || "Title*"}
                name="title"
                rules={[
                  {
                    required: true,
                    message: `Please enter title!`,
                  },
                ]}
              >
                <Input placeholder={"Title"} />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Unit*"
                name="unit"
                rules={[
                  {
                    required: true,
                    message: "Please select a unit!",
                  },
                ]}
              >
                <TreeSelect
                  showSearch
                  treeLine={true}
                  dropdownStyle={{
                    maxHeight: 400,
                    overflow: "auto",
                  }}
                  treeData={unitsTree}
                  placeholder="Select unit"
                  treeDefaultExpandAll
                  treeNodeFilterProp={"title"}
                  showIcon={false}
                  filterTreeNode
                  onChange={(value) => selectUnit(value)}
                />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                label="Assign to*"
                name="assignTo"
                rules={[
                  {
                    required: true,
                    message: "Please select a position!",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select position"
                  optionFilterProp="children"
                  onChange={(value) => selectAssignTo(value)}
                  filterOption={(input, option) =>
                    option.children
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                  }
                  filterSort={(optionA, optionB) =>
                    optionA.children
                      .toLowerCase()
                      .localeCompare(optionB.children.toLowerCase())
                  }
                >
                  {unitPositions.map((position) => {
                    return (
                      <Option key={position.id} value={position.id}>
                        {(position.user?.firstName
                          ? position.user?.firstName + ", "
                          : "") + position.designation?.name}
                      </Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>

            <>
              <Col span={12}>
                <Form.Item
                  label="Target scale*"
                  name="targetScale"
                  rules={[
                    {
                      required: true,
                      message: "Please select a target scale!",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Select a scale"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    filterSort={(optionA, optionB) =>
                      optionA.children
                        .toLowerCase()
                        .localeCompare(optionB.children.toLowerCase())
                    }
                    onChange={(value) => selectScale(value)}
                  >
                    {scales.map((scale) => {
                      return (
                        <Option key={scale.id} value={scale.id}>
                          {scale.name}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
              <>
                <Col span={8}>
                  <Form.Item
                    label={fieldLabels.target || "Target"}
                    name="target"
                    rules={[
                      {
                        required: true,
                        message: "Please enter target!",
                      },
                    ]}
                  >
                    <InputNumber
                      min={4}
                      addonAfter={
                        addScaleUnit ? unitSelector : percentage && "%"
                      }
                      style={{ width: "100%" }}
                      formatter={(value) => {
                        if (!value) return '';
                        // Format number with commas for better readability
                        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
                      }}
                      parser={(value) => {
                        if (!value) return '';
                        // Remove commas when parsing
                        return value.replace(/,/g, '');
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Form.Item label=" ">
                    <div className="flex items-center ">
                      <Checkbox
                        checked={canExceed}
                        disabled={isNegativeScale} // Disable when negative scale is enabled
                        onChange={(e) => setCanExceed(e.target.checked)}
                      >
                        Exceed?
                      </Checkbox>

                      <Popover
                        placement="topRight"
                        content={
                          <div className="text-xs">Exceed while cascading</div>
                        }
                        trigger="hover"
                      >
                        <div className="text-gray-500 text-xs ml-1">
                          <InfoCircleOutlined />
                        </div>
                      </Popover>
                    </div>
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item label=" " name="isPercentageUpdate" valuePropName="checked">
                    <div className="flex items-center">
                      <Checkbox
                        checked={isPercentageUpdate}
                        disabled={isNegativeScale} // Disable when negative scale is enabled
                        onChange={(e) => setIsPercentageUpdate(e.target.checked)}
                      >
                        Update by %
                      </Checkbox>

                      <Popover
                        placement="topRight"
                        content={
                          <div className="text-xs">When enabled, progress updates will be entered as percentages (0-100%)</div>
                        }
                        trigger="hover"
                      >
                        <div className="text-gray-500 text-xs ml-1">
                          <InfoCircleOutlined />
                        </div>
                      </Popover>
                    </div>
                  </Form.Item>
                </Col>

                {supportsNegativeScale && (
                  <Col span={5}>
                    <Form.Item label=" " name="isNegativeScale" valuePropName="checked">
                      <div className="flex items-center">
                        <Checkbox
                          disabled={isPercentageUpdate || canExceed} // Disable when percentage update or exceed is enabled
                          onChange={(e) => setIsNegativeScale(e.target.checked)}
                        >
                          Negative Scale
                        </Checkbox>

                        <Popover
                          placement="topRight"
                          content={
                            <div className="text-xs">
                              Enables countdown logic for loss reduction, debt paydown, and cost cutting scenarios. 
                              Progress shows remaining amount instead of achieved amount.
                              <br /><br />
                              <strong>Available for:</strong> Number, Currency, and Percentage scales only.
                            </div>
                          }
                          trigger="hover"
                        >
                          <div className="text-gray-500 text-xs ml-1">
                            <InfoCircleOutlined />
                          </div>
                        </Popover>
                      </div>
                    </Form.Item>
                  </Col>
                )}
              </>
            </>

            {topPlan?.description && (
              <Col span={24}>
                <Form.Item label={fieldLabels.description || "Description"} name="description">
                  <TextArea
                    showCount
                    maxLength={100}
                    placeholder="Description"
                  />
                </Form.Item>
              </Col>
            )}

            {topPlan?.targetDate && (
              <Col span={12}>
                <Form.Item label="Target date*" name="targetDate">
                  <DatePicker className="w-full" placeholder="Target date" />
                </Form.Item>
              </Col>
            )}

            {isPhrmEnabled && (
              <>
                <Col span={12}>
                  <Form.Item
                    label="Employee Reference"
                    name="empReference"
                  >
                    <Input 
                      placeholder="Auto-filled from assigned user" 
                      disabled
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Evaluation"
                    name="ehrmEvalId"
                    rules={[{ required: false }]}
                  >
                    <Select
                      placeholder="Select evaluation"
                      onChange={(value) => selectEvaluation(value)}
                      allowClear
                      disabled={!assignedUser?.empReferrenceId}
                    >
                      {evaluations.map((evaluation) => (
                        <Option key={evaluation.EVAL_ID} value={evaluation.EVAL_ID}>
                          {evaluation.EVAL_NAME}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Goal Group"
                    name="ehrmGoalTypeId"
                    rules={[{ required: false }]}
                  >
                    <Select 
                      placeholder="Select goal group" 
                      allowClear
                      disabled={!selectedEvalId}
                    >
                      {goalGroups.map((group) => (
                        <Option key={group.GGRP_ID} value={group.GGRP_ID}>
                          {group.GGRP_NAME}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Goal Weight (%)"
                    name="ehrmGoalWeightage"
                    rules={[{ required: false }]}
                  >
                    <InputNumber
                      min={0}
                      max={100}
                      precision={2}
                      placeholder="0.00"
                      style={{ width: '100%' }}
                      disabled={!assignedUser?.empReferrenceId}
                    />
                  </Form.Item>
                </Col>
              </>
            )}

            {renderAttributes()}

            {!topPlan && <PageLoader />}
            {positionState?.userRole?.name === "Manager" && (
              <Col span={12}>
                <Form.Item
                  label="Approver"
                  name="approver"
                  rules={[
                    {
                      required: true,
                      message: "Please select approver!",
                    },
                  ]}
                >
                  <Select
                    allowClear
                    showSearch
                    placeholder="Select approver"
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    filterSort={(optionA, optionB) =>
                      optionA.children
                        .toLowerCase()
                        .localeCompare(optionB.children.toLowerCase())
                    }
                  >
                    {approvers?.map((approver) => {
                      return (
                        <Option key={approver?.id} value={approver?.id}>
                          {(approver?.designations?.abbreviation ||
                            approver?.designations?.name) +
                            "," +
                            approver?.units?.name}
                        </Option>
                      );
                    })}
                  </Select>
                </Form.Item>
              </Col>
            )}
            
            {topPlan?.cascadeFromSplitPlansEnabled && (
              <Col span={24}>
                <Form.Item
                  label="Cascade from Split Plans"
                  name="cascadeFromSplitPlans"
                  valuePropName="checked"
                  tooltip="When enabled, split plans will contribute to this plan's progress and cascade plans cannot be created below this plan"
                >
                  <Checkbox onChange={(e) => setCascadeFromSplitPlans(e.target.checked)}>
                    Enable split plans to cascade progress to this plan
                  </Checkbox>
                </Form.Item>
              </Col>
            )}
            
            <Col span={24}>
              <Form.Item style={{ marginBottom: 0 }}>
                <div className=" flex justify-end">
                  <Button className="mr-3 w-28" onClick={() => handleCancel()}>
                    Cancel
                  </Button>
                  <Button
                    className="btn w-32"
                    type="primary"
                    htmlType={submit && "submit"}
                    disabled={submit}
                  >
                    {submit ? (
                      <Spin
                        size="small"
                        indicator={
                          <LoadingOutlined style={{ color: "#fff" }} spin />
                        }
                      />
                    ) : (
                      "Add"
                    )}
                  </Button>
                </div>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}

export default AddTopPlanModal;
